import { useState } from 'react'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Textarea } from '../ui/textarea'
import { Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CoverPromptDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (promptTemplate: string, options?: { model?: string; useDescription?: boolean }) => void
  title: string
  author: string
  description?: string  // Book description for templates that use it
}

// Prompt templates with {title}, {author}, and optional {description} placeholders
interface PromptTemplate {
  id: string
  name: string
  description: string
  template: string
  model?: string  // Optional: specific model to use
  useDescription?: boolean  // Whether this template uses {description} placeholder
}

const PROMPT_TEMPLATES: PromptTemplate[] = [
  {
    id: 'nano-banana-3-pro',
    name: 'Nano Banana 3 Pro',
    description: 'Realistic cover with genre awareness (uses Gemini 3 Pro)',
    model: 'gemini-3-pro-image-preview',
    useDescription: true,
    template: `Generate a realistic ebook cover for:

Book Title: {title}
Author: {author}

The cover should include the title "{title}" and author name "{author}" prominently displayed.
Create a book cover that clearly communicates the genre and appeals to the target audience, using a strong visual concept, appropriate mood, and readable typography. 
Include the title, and author name with clear hierarchy, and design the layout so it remains attractive and legible even as a small thumbnail in an online store. 
Deliver the design in high resolution following the required aspect ratio.
Use a color palette and illustration or photography style that match the book's tone from description below: 
{description}
`
  },
  {
    id: 'fancy',
    name: 'Fancy & Artistic',
    description: 'Detailed, artistic cover with prominent text',
    template: `Generate a fancy ebook cover for an online library.

Book Title: {title}
Author: {author}

The cover should include the title "{title}" and author name "{author}" prominently displayed.
Create an artistic, professional-looking book cover suitable for an ebook library.`
  },
  {
    id: 'simple',
    name: 'Simple & Clear',
    description: 'Clean design with clear, readable text',
    template: `Generate a simple and clean ebook cover.

Book Title: {title}
Author: {author}

Create a minimalist book cover with the title "{title}" and author "{author}" clearly displayed. Use a clean design with good contrast and readable text.`
  },
  {
    id: 'graphical',
    name: 'Graphical & Modern',
    description: 'Visual focus with minimal text details',
    template: `Generate a modern graphical ebook cover.

Book Title: {title}
Author: {author}

Create a visually striking cover with bold graphics and minimal text. Include the title "{title}" and author "{author}" in a modern, stylish way. Focus on visual impact over text details.`
  }
]

export function CoverPromptDialog({ open, onClose, onConfirm, title, author, description }: CoverPromptDialogProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('nano-banana-3-pro')
  const [customPrompt, setCustomPrompt] = useState<string>('')

  // Get the selected template
  const selectedTemplate = PROMPT_TEMPLATES.find(t => t.id === selectedTemplateId) || PROMPT_TEMPLATES[0]
  
  // Get the current prompt (either custom or from template)
  const currentPrompt = customPrompt || selectedTemplate.template

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId)
    // Reset custom prompt when selecting a template
    setCustomPrompt('')
  }

  const handleConfirm = () => {
    const finalPrompt = customPrompt || selectedTemplate.template
    // Pass model and useDescription options if template has them (and no custom prompt)
    const options = !customPrompt ? {
      model: selectedTemplate.model,
      useDescription: selectedTemplate.useDescription
    } : undefined
    onConfirm(finalPrompt, options)
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="cover-prompt-dialog max-w-3xl max-h-[90vh] overflow-hidden flex flex-col [&>button]:hidden" 
        onInteractOutside={(e) => e.preventDefault()} 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Choose Cover Prompt Template
          </DialogTitle>
          <DialogDescription>
            Select a prompt template or customize it. Use {'{title}'}, {'{author}'}, and {'{description}'} as placeholders.
          </DialogDescription>
        </DialogHeader>

        {/* Template Tiles */}
        <div className="template-tiles grid grid-cols-1 md:grid-cols-3 gap-3 py-4">
          {PROMPT_TEMPLATES.map((template) => (
            <button
              key={template.id}
              type="button"
              onClick={() => handleTemplateSelect(template.id)}
              className={cn(
                'template-tile p-4 rounded-lg border-2 text-left transition-all',
                'hover:border-primary/50 hover:bg-accent',
                selectedTemplateId === template.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-background'
              )}
            >
              <div className="font-semibold text-sm mb-1">{template.name}</div>
              <div className="text-xs text-muted-foreground">{template.description}</div>
            </button>
          ))}
        </div>

        {/* Prompt Preview/Editor */}
        <div className="prompt-editor flex-1 flex flex-col min-h-0">
          <label className="text-sm font-medium mb-2">Prompt Template</label>
          <Textarea
            value={currentPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Enter your custom prompt template..."
            className="flex-1 min-h-[200px] font-mono text-sm"
          />
          <div className="text-xs text-muted-foreground mt-2">
            Preview with values: <code className="bg-muted px-1 rounded">{title}</code>, <code className="bg-muted px-1 rounded">{author}</code>
            {selectedTemplate.useDescription && (
              <>, and <code className="bg-muted px-1 rounded">{description ? (description.length > 50 ? description.slice(0, 50) + '...' : description) : '(no description)'}</code></>
            )}
            {selectedTemplate.model && (
              <span className="ml-2 text-primary">• Model: {selectedTemplate.model}</span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Generate Cover
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

